// apps/client/src/pages/auth/login-adm/page.tsx

import { useLoginAdm } from "@/client/services/auth/login-adm";
import { useFeatureFlags } from "@/client/services/feature";
import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema } from "@reactive-resume/dto";
import { usePasswordToggle } from "@reactive-resume/hooks";
import {
  Alert,
  AlertTitle,
  Button,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Form,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { EnvelopeIcon, LockKeyIcon, SignInIcon, ShieldCheckIcon } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import z from "zod";

type FormValues = z.infer<typeof loginSchema>;

export const LoginAdmPage = () => {
  const { login, loading } = useLoginAdm();
  const { flags } = useFeatureFlags();

  const formRef = useRef<HTMLFormElement>(null);
  usePasswordToggle(formRef);

  const form = useForm<FormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      identifier: "",
      password: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await login(data);
    } catch (error) {
      form.reset();
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-admin-background p-4">
      <Helmet>
        <title>{"Connexion à l'espace d'administration"}</title>
      </Helmet>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-md"
      >
        {/* En-tête avec logo et titre */}
        <div className="mb-8 flex flex-col items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-admin-primary/10">
            <ShieldCheckIcon size={32} weight="duotone" className="text-admin-primary" />
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold tracking-tight text-admin-text-primary">
              Espace d'administration
            </h1>
            <p className="mt-1 text-sm text-admin-text-muted">
              Connectez-vous pour accéder au tableau de bord
            </p>
          </div>
        </div>

        {/* Carte de connexion */}
        <div className="rounded-xl-admin border border-admin-border bg-admin-surface p-6 shadow-sm-admin">
          {flags.isEmailAuthDisabled && (
            <Alert variant="error" className="mb-4">
              <AlertTitle>
                La connexion par email est actuellement désactivée par l'administrateur.
              </AlertTitle>
            </Alert>
          )}

          <div
            className={cn(flags.isEmailAuthDisabled && "pointer-events-none select-none blur-sm")}
          >
            <Form {...form}>
              <form
                ref={formRef}
                className="flex flex-col gap-y-4"
                onSubmit={form.handleSubmit(onSubmit)}
              >
                {/* Champ Email */}
                <FormField
                  name="identifier"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-admin-text-secondary">
                        Adresse email
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <EnvelopeIcon
                            size={18}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
                          />
                          <Input
                            autoComplete="email"
                            className="h-11 w-full bg-admin-background pl-10 lowercase text-admin-text-primary placeholder:text-admin-text-muted"
                            placeholder="vous@exemple.com"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Champ Mot de passe */}
                <FormField
                  name="password"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-admin-text-secondary">
                        Mot de passe
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <LockKeyIcon
                            size={18}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
                          />
                          <Input
                            type="password"
                            autoComplete="password"
                            className="h-11 w-full bg-admin-background pl-10 text-admin-text-primary placeholder:text-admin-text-muted"
                            placeholder="••••••••"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormDescription className="text-xs text-admin-text-muted">
                        Maintenez{" "}
                        <kbd className="rounded border border-admin-border bg-admin-surface-elevated px-1.5 py-0.5 text-xs font-semibold">
                          Ctrl
                        </kbd>{" "}
                        pour afficher temporairement votre mot de passe.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Bouton de connexion */}
                <div className="mt-2">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="h-11 w-full bg-admin-primary text-admin-primary-foreground hover:bg-admin-primary/90"
                  >
                    <SignInIcon size={18} weight="bold" className="mr-2" />
                    {loading ? "Connexion en cours..." : "Se connecter"}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </div>

        {/* Pied de page */}
        <p className="mt-6 text-center text-xs text-admin-text-muted">
          Accès réservé aux administrateurs autorisés.
        </p>
      </motion.div>
    </div>
  );
};
